import { Button, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { checkHealthcareInstitutionIdDuplicate, updatePharmacy, uploadFile } from "../../../../api";
import { colors } from "../../../../styles";
import { IPharmacy } from "../../../../types";
import { getApiErrorCode } from "../../../../utils";
import FileAttach from "../../../Auth/SignUp/FileAttach";
import { getSignUpErrorMessage } from "../../../Auth/SignUp/errors";
import { FieldMessage, underlineInputProps } from "../../../Auth/SignUp/StepLayout";

interface RejectedPharmacyModalProps {
    isOpen: boolean;
    onClose: () => void;
    pharmacy: IPharmacy;
}

interface IPharmacyForm {
    bizRegNo: string;
    healthcareInstitutionId: string;
}

const healthcareInstitutionIdRegex = /^\d{8}$/;
const bizRegNoRegex = /^\d{3}-?\d{2}-?\d{5}$/;

export default function RejectedPharmacyModal({ isOpen, onClose, pharmacy }: RejectedPharmacyModalProps) {
    const queryClient = useQueryClient();
    const toast = useToast();
    const [businessRegistrationCertImage, setBusinessRegistrationCertImage] = useState<File | null>(null);
    const [establishmentRegistrationCertImage, setEstablishmentRegistrationCertImage] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [debouncedHealthcareInstitutionId, setDebouncedHealthcareInstitutionId] = useState("");

    const needsBizRegNo = !pharmacy.bizRegNoIsValid;
    const needsHealthcareInstitutionId = !pharmacy.healthcareInstitutionIdIsValid;
    const needsBusinessImage = !pharmacy.businessRegistrationCertImageIsValid;
    const needsEstablishmentImage = !pharmacy.establishmentRegistrationCertImageIsValid;

    const { register, handleSubmit, watch, reset } = useForm<IPharmacyForm>({
        defaultValues: {
            bizRegNo: pharmacy.bizRegNo,
            healthcareInstitutionId: pharmacy.healthcareInstitutionId,
        },
    });
    const bizRegNo = watch("bizRegNo") ?? "";
    const healthcareInstitutionId = watch("healthcareInstitutionId") ?? "";
    const isBizRegNoFormatValid = bizRegNoRegex.test(bizRegNo);
    const isHealthcareInstitutionIdFormatValid = healthcareInstitutionIdRegex.test(healthcareInstitutionId);
    const isSameAsCurrentBizRegNo = bizRegNo === pharmacy.bizRegNo;
    const isSameAsCurrentHealthcareInstitutionId = healthcareInstitutionId === pharmacy.healthcareInstitutionId;

    useEffect(() => {
        if (isOpen) {
            reset({
                bizRegNo: pharmacy.bizRegNo,
                healthcareInstitutionId: pharmacy.healthcareInstitutionId,
            });
            setBusinessRegistrationCertImage(null);
            setEstablishmentRegistrationCertImage(null);
            setErrorMessage("");
        }
    }, [isOpen, pharmacy, reset]);

    useEffect(() => {
        const id = setTimeout(() => setDebouncedHealthcareInstitutionId(healthcareInstitutionId), 400);
        return () => clearTimeout(id);
    }, [healthcareInstitutionId]);

    const {
        isLoading: isCheckingHealthcareInstitutionId,
        isError: isHealthcareInstitutionIdCheckError,
        error: healthcareInstitutionIdCheckError,
    } = useQuery({
        queryKey: ["checkHealthcareInstitutionIdDuplicate", debouncedHealthcareInstitutionId],
        queryFn: () => checkHealthcareInstitutionIdDuplicate({ healthcareInstitutionId: debouncedHealthcareInstitutionId }),
        enabled:
            isOpen &&
            needsHealthcareInstitutionId &&
            !!debouncedHealthcareInstitutionId &&
            healthcareInstitutionIdRegex.test(debouncedHealthcareInstitutionId) &&
            !isSameAsCurrentHealthcareInstitutionId,
        retry: false,
    });

    const isHealthcareInstitutionIdDuplicate =
        isHealthcareInstitutionIdCheckError &&
        getApiErrorCode(healthcareInstitutionIdCheckError) === "HEALTHCARE_INSTITUTION_ALREADY_USED";
    const isHealthcareInstitutionIdCheckUnavailable =
        isHealthcareInstitutionIdCheckError && !isHealthcareInstitutionIdDuplicate;

    const checkHealthcareInstitutionIdDuplicateMutation = useMutation({
        mutationFn: checkHealthcareInstitutionIdDuplicate,
        retry: false,
    });
    const uploadFileMutation = useMutation({ mutationFn: uploadFile });
    const updatePharmacyMutation = useMutation({
        mutationFn: updatePharmacy,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["me"] });
            toast({
                title: "약국 정보가 다시 제출되었습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            onClose();
        },
    });

    const bizRegNoHint = (() => {
        if (!needsBizRegNo || !bizRegNo) return null;
        if (!isBizRegNoFormatValid) {
            return { message: "사업자등록번호는 XXX-XX-XXXXX 형식으로 입력해주세요.", tone: "error" as const };
        }
        if (isSameAsCurrentBizRegNo) {
            return { message: "현재 등록된 사업자등록번호입니다. 올바른 번호로 수정해주세요.", tone: "muted" as const };
        }
        return null;
    })();

    const healthcareInstitutionHint = (() => {
        if (!needsHealthcareInstitutionId || !healthcareInstitutionId) return null;
        if (!isHealthcareInstitutionIdFormatValid) {
            return { message: "요양기관 번호는 8자리 숫자로 입력해주세요.", tone: "error" as const };
        }
        if (isSameAsCurrentHealthcareInstitutionId) {
            return { message: "현재 등록된 요양기관 번호입니다. 올바른 번호로 수정해주세요.", tone: "muted" as const };
        }
        if (isCheckingHealthcareInstitutionId) {
            return { message: "중복 확인 중...", tone: "muted" as const };
        }
        if (isHealthcareInstitutionIdDuplicate) {
            return { message: "이미 사용 중인 요양기관 번호입니다.", tone: "error" as const };
        }
        if (isHealthcareInstitutionIdCheckUnavailable) {
            return { message: "요양기관 번호 중복 확인에 실패했습니다.", tone: "error" as const };
        }
        return { message: "사용 가능한 요양기관 번호입니다.", tone: "success" as const };
    })();

    const onSubmit = async (data: IPharmacyForm) => {
        if (needsBusinessImage && !businessRegistrationCertImage) {
            setErrorMessage("사업자 등록증을 첨부해주세요.");
            return;
        }
        if (needsEstablishmentImage && !establishmentRegistrationCertImage) {
            setErrorMessage("약국 개설 등록증을 첨부해주세요.");
            return;
        }

        setErrorMessage("");

        try {
            const payload: {
                bizRegNo?: string;
                healthcareInstitutionId?: string;
                businessRegistrationCertImage?: string;
                establishmentRegistrationCertImage?: string;
            } = {};

            if (needsBizRegNo) {
                payload.bizRegNo = data.bizRegNo;
            }
            if (needsHealthcareInstitutionId) {
                if (!isSameAsCurrentHealthcareInstitutionId) {
                    await checkHealthcareInstitutionIdDuplicateMutation.mutateAsync({
                        healthcareInstitutionId: data.healthcareInstitutionId,
                    });
                }
                payload.healthcareInstitutionId = data.healthcareInstitutionId;
            }
            if (needsBusinessImage && businessRegistrationCertImage) {
                payload.businessRegistrationCertImage = await uploadFileMutation.mutateAsync({
                    file: businessRegistrationCertImage,
                });
            }
            if (needsEstablishmentImage && establishmentRegistrationCertImage) {
                payload.establishmentRegistrationCertImage = await uploadFileMutation.mutateAsync({
                    file: establishmentRegistrationCertImage,
                });
            }

            if (
                !payload.bizRegNo &&
                !payload.healthcareInstitutionId &&
                !payload.businessRegistrationCertImage &&
                !payload.establishmentRegistrationCertImage
            ) {
                setErrorMessage("수정할 약국 정보가 없습니다.");
                return;
            }

            await updatePharmacyMutation.mutateAsync(payload);
        } catch (error) {
            setErrorMessage(getSignUpErrorMessage(error, "약국 정보 보완 중 문제가 발생했습니다."));
        }
    };

    const canSubmit =
        (needsBizRegNo || needsHealthcareInstitutionId || needsBusinessImage || needsEstablishmentImage) &&
        (!needsBizRegNo ||
            (isBizRegNoFormatValid && !isSameAsCurrentBizRegNo)) &&
        (!needsHealthcareInstitutionId ||
            (isHealthcareInstitutionIdFormatValid &&
                (!isHealthcareInstitutionIdCheckError || isSameAsCurrentHealthcareInstitutionId))) &&
        (!needsBusinessImage || Boolean(businessRegistrationCertImage)) &&
        (!needsEstablishmentImage || Boolean(establishmentRegistrationCertImage)) &&
        !isCheckingHealthcareInstitutionId;

    return (
        <Modal motionPreset="slideInBottom" isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text fontSize={20} fontWeight={700} color={colors.black}>약국 정보 보완하기</Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody padding={10} as="form" onSubmit={handleSubmit(onSubmit)}>
                    {pharmacy.rejectedReason && (
                        <Text fontSize={14} fontWeight={500} color={colors.apple} marginBottom={5}>
                            {pharmacy.rejectedReason}
                        </Text>
                    )}
                    <Text fontSize={12} fontWeight={500} color={colors.g3} marginBottom={5}>
                        유효하지 않은 항목만 다시 제출할 수 있습니다.
                    </Text>
                    {needsBizRegNo && (
                        <FormControl>
                            <FormLabel fontSize="sm" color={colors.blue}>사업자등록번호</FormLabel>
                            <Input
                                {...register("bizRegNo", { required: true })}
                                type="text"
                                placeholder="000-00-00000"
                                {...underlineInputProps}
                            />
                            <FieldMessage message={bizRegNoHint?.message} tone={bizRegNoHint?.tone} />
                        </FormControl>
                    )}
                    {needsHealthcareInstitutionId && (
                        <FormControl marginTop={needsBizRegNo ? 5 : 0}>
                            <FormLabel fontSize="sm" color={colors.blue}>요양기관 번호</FormLabel>
                            <Input
                                {...register("healthcareInstitutionId", { required: true })}
                                type="text"
                                placeholder="요양기관 번호"
                                {...underlineInputProps}
                            />
                            <FieldMessage message={healthcareInstitutionHint?.message} tone={healthcareInstitutionHint?.tone} />
                        </FormControl>
                    )}
                    {(needsBusinessImage || needsEstablishmentImage) && (
                        <FormControl marginTop={needsBizRegNo || needsHealthcareInstitutionId ? 8 : 0}>
                            <FormLabel fontSize="sm" color={colors.blue}>증명 서류</FormLabel>
                            {needsBusinessImage && (
                                <FileAttach
                                    label="01 | 사업자 등록증"
                                    inputId="rejected-business-upload"
                                    file={businessRegistrationCertImage}
                                    onChange={(file) => {
                                        setBusinessRegistrationCertImage(file);
                                        setErrorMessage("");
                                    }}
                                />
                            )}
                            {needsEstablishmentImage && (
                                <FileAttach
                                    label="02 | 약국 개설 등록증"
                                    inputId="rejected-establishment-upload"
                                    file={establishmentRegistrationCertImage}
                                    onChange={(file) => {
                                        setEstablishmentRegistrationCertImage(file);
                                        setErrorMessage("");
                                    }}
                                    showDivider={needsBusinessImage}
                                />
                            )}
                        </FormControl>
                    )}
                    {!needsBizRegNo && !needsHealthcareInstitutionId && !needsBusinessImage && !needsEstablishmentImage && (
                        <Text fontSize={14} color={colors.g3}>보완할 항목이 없습니다.</Text>
                    )}
                    <FieldMessage message={errorMessage} />
                    <Stack marginTop={8} width="100%">
                        <Button
                            type="submit"
                            isLoading={
                                uploadFileMutation.isPending ||
                                updatePharmacyMutation.isPending ||
                                checkHealthcareInstitutionIdDuplicateMutation.isPending
                            }
                            isDisabled={!canSubmit}
                            width="100%"
                            height="52px"
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                        >
                            제출
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
